import React, { useEffect, useState } from 'react'
import { Form } from 'react-final-form'
import { useMutation } from '@apollo/react-hooks'
import { isEmpty } from 'lodash'
import { up } from 'styled-breakpoints'
import styled from 'styled-components'

import { Media, Price, Rating } from '~/components'
import Modal from '~/components/modal'
import useModalManager from '~/hooks/use-modal-manager'
import Button from '~/truck/button'
import Icon from '~/truck/icon'
import Input from '~/truck/input'
import notifier from '~/truck/notifier'
import TextArea from '~/truck/text-area'
import theme from '~/truck/theme'
import cloudinary from '~/utils/cloudinary'
import { messagesRatingWhenHovering, transformations } from '~/utils/constants'

import upsertReviewMutation from '../../gql/mutations/upsert-review'

import RatingMessage from './rating-message'

const StyledTextArea = styled(TextArea)`
  height: ${theme.spacing.cozy * 6}px;
  padding: ${theme.spacing.compact * 2.5}px ${theme.spacing.comfortable}px;
  resize: none;
`

const ProductDetail = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  width: 100%;
`

const Image = styled.img`
  height: ${theme.spacing.cozy * 9}px;
  object-fit: cover;
  width: ${theme.spacing.cozy * 9}px;
`

const ImageWrapper = styled.div`
  align-items: center;
  display: flex;
  height: 100%;
`

const Product = styled.div`
  background-color: ${theme.colors.grayLight};
  border-radius: ${theme.spacing.compact}px;
  display: flex;
  height: ${theme.spacing.cozy * 15}px;
  margin-bottom: ${theme.spacing.cozy * 3}px;
  padding: ${theme.spacing.cozy * 2}px;

  ${ProductDetail} {
    margin-left: ${theme.spacing.cozy * 3}px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: ${theme.spacing.cozy * 3}px;
`

const StyledRating = styled(Rating)`
  > span {
    > span {
      margin: 0 ${theme.spacing.cozy}px;
    }

    > :first-child {
      margin-left: 0;
    }
  }
`

const Qualification = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${theme.spacing.cozy * 3}px;

  ${up('lg')} {
    align-items: flex-start;
  }

  > :first-child {
    align-items: center;
    display: flex;
    justify-content: space-between;
    margin-bottom: ${theme.spacing.cozy * 2}px;
  }

  > :last-child {
    align-items: center;
    display: flex;
    justify-content: center;
  }
`

const Title = styled.div`
  margin-bottom: ${theme.spacing.cozy * 3}px;

  p {
    margin-bottom: ${theme.spacing.cozy}px;
  }
`

const Comment = styled.div`
  margin-bottom: ${theme.spacing.cozy * 15}px;

  p {
    margin-bottom: ${theme.spacing.cozy}px;
  }

  ${up('lg')} {
    margin-bottom: 0;
  }
`

const ButtonsContainer = styled.div`
  align-items: center;
  background-color: ${theme.colors.white};
  bottom: 0;
  box-shadow: 0px -2px 5px rgba(0, 0, 0, 0.08);
  display: flex;
  height: ${theme.spacing.cozy * 10}px;
  left: 0;
  position: fixed;
  right: 0;

  > button {
    margin: 0 ${theme.spacing.cozy * 2}px;
  }

  > :last-child {
    margin-left: 0;
  }

  ${up('lg')} {
    justify-content: flex-end;

    button {
      margin: 0;
      width: ${theme.spacing.cozy * 19}px;
    }

    > :last-child {
      margin: 0 ${theme.spacing.cozy * 3}px;
    }
  }
`

const StyledModal = styled(Modal)`
  ${up('lg')} {
    min-height: ${theme.spacing.cozy * 78}px;
    padding: ${theme.spacing.cozy * 3}px;
    width: ${theme.spacing.cozy * 77}px;
  }
`

const UpsertReviewModal = props => {
  const { product, initialValues } = props

  const isNewReview = isEmpty(initialValues)

  const [upsertReview, { loading }] = useMutation(upsertReviewMutation)

  const [hoverMessage, setHoverMessage] = useState(undefined)

  const [rating, setRating] = useState(initialValues.rating || 5)

  const [comment, setComment] = useState(initialValues.comment || '')

  const [title, setTitle] = useState(initialValues.title || '')

  const modalManager = useModalManager()

  useEffect(() => {
    setHoverMessage(messagesRatingWhenHovering[rating - 1])
  }, [rating])

  const onClose = () => {
    modalManager.closeModal()
  }

  const onSubmit = async () => {
    const options = {
      variables: {
        productId: product.id,
        title,
        comment,
        rating,
      },
      refetchQueries: ['productReviews'],
      awaitRefetchQueries: true,
    }

    await upsertReview(options)

    if (isNewReview) {
      notifier.success('¡Reseña creada exitósamente!')
    } else {
      notifier.success('¡Reseña actualizada exitósamente!')
    }

    onClose()
  }

  const inputChange = (event, setter) => {
    setter(event.target.value)
  }

  const onClickRating = value => {
    setRating(value)

    setHoverMessage(messagesRatingWhenHovering[value - 1])
  }

  const onHoverRating = value => {
    if (value) {
      setHoverMessage(messagesRatingWhenHovering[value - 1])
    } else {
      setHoverMessage(messagesRatingWhenHovering[rating - 1])
    }
  }

  const photo = cloudinary.applyTransformations(product.defaultPhoto, transformations.TINY_PRODUCT)

  return (
    <StyledModal>
      <Form onSubmit={onSubmit}>
        {formProps => {
          const { handleSubmit } = formProps

          return (
            <div>
              <Header>
                <p className="text-xl font-semibold">
                  {isNewReview ? 'Escribir una reseña del producto' : 'Actualizar reseña del producto'}
                </p>
                <Icon type="close" color="black" onClick={onClose} />
              </Header>
              <Product>
                <ImageWrapper>
                  <Image src={photo} alt={product.name} />
                </ImageWrapper>
                <ProductDetail>
                  <p className="font-semibold">{product.name}</p>
                  <p>{product.presentation}</p>
                  <Price
                    allowTaxLabel
                    oldValue={product.pricing.oldPrice}
                    value={product.pricing.price}
                    textStyle="h5Semibold"
                  />
                </ProductDetail>
              </Product>
              <Qualification>
                <div>
                  <p className="font-semibold">Califica este producto</p>
                  {hoverMessage && <Media mobile={<RatingMessage {...hoverMessage} />} />}
                </div>
                <div>
                  <StyledRating
                    initialRating={rating}
                    readonly={false}
                    onClick={onClickRating}
                    onHover={onHoverRating}
                    imageStyle={{
                      width: theme.spacing.cozy * 4,
                      height: theme.spacing.cozy * 4,
                    }}
                  />
                  {hoverMessage && <Media desktop={<RatingMessage {...hoverMessage} />} />}
                </div>
              </Qualification>
              <Title>
                <p className="font-semibold">Título</p>
                <Input
                  placeholder="¡El producto está increíble!"
                  value={title}
                  onChange={event => inputChange(event, setTitle)}
                />
              </Title>
              <Comment>
                <p className="font-semibold">Comentarios</p>
                <StyledTextArea
                  placeholder="Es de buena calidad..."
                  value={comment}
                  onChange={event => inputChange(event, setComment)}
                />
              </Comment>
              <ButtonsContainer>
                <Button.Ghost color="secondary" onClick={onClose}>
                  Cancelar
                </Button.Ghost>
                <Button type="submit" color="primary" loading={!!loading} onClick={handleSubmit}>
                  {isNewReview ? 'Publicar' : 'Actualizar'}
                </Button>
              </ButtonsContainer>
            </div>
          )
        }}
      </Form>
    </StyledModal>
  )
}

export default UpsertReviewModal
